import React, { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import Personas from "@/components/cosec/Personas";
import FeatureTabs from "@/components/site/FeatureTabs";
import { FEATURE_MODULES, FEATURE_CATEGORIES, COMPARE_ROWS } from "@/constants/siteData";
import { SID } from "@/constants/siteIds";

function CompareCell({ v }) {
  if (v === true) return <span className="inline-flex items-center gap-1 text-accent font-bold"><CheckCircle2 className="h-4 w-4" /> Yes</span>;
  if (v === false) return <span className="text-muted-foreground/60">-</span>;
  return <span className="text-muted-foreground italic">{String(v)}</span>;
}

export default function FeaturesPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [hash]);

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="dark-hero relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-[0.08] pointer-events-none" />

        <div className="container-cv relative pt-14 md:pt-24 pb-12 md:pb-20 text-center max-w-4xl mx-auto">
          <span className="pill">
            <Sparkles className="h-3.5 w-3.5 text-accent" /> 14 modules · One platform
          </span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04]">
            Everything your Cosec, finance and{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-accent">legal teams</span>
              <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
            </span>{" "}
            need.
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">
            Statutory registers, Companies House filings, board meetings, e-signatures, workflow automation and AI-assisted documents, all in one secure platform built for UK companies.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild data-testid={SID.featuresHeroCta} className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-bold">
              <a href="/contact">
                Book a Demo <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button asChild variant="outline" className="h-12 px-6 border-white/30 text-white hover:bg-white hover:text-[hsl(207_74%_21%)] font-semibold bg-white/[0.04]">
              <Link to="/pricing">See Pricing</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Personas, built for the teams that keep businesses compliant */}
      <Personas />

      {/* Category sections, tabbed display per category */}
      {FEATURE_CATEGORIES.map((cat) => {
        const items = FEATURE_MODULES.filter((f) => f.category === cat.key).map((f) => ({
          key: f.key,
          Icon: f.Icon,
          title: f.title,
          desc: f.desc,
          href: `/${f.key}`,
        }));
        return (
          <div
            key={cat.key}
            id={cat.key}
            data-testid={SID.featureCategory(cat.key)}
            className="border-t border-border odd:bg-secondary/30"
          >
            <FeatureTabs
              testId={`feature-tabs-${cat.key}`}
              eyebrow={cat.label}
              heading={cat.desc}
              items={items}
            />
          </div>
        );
      })}

      {/* Comparison */}
      <section className="py-20 md:py-24 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-[0.06] pointer-events-none" />
        <div className="container-cv relative">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-accent">
              <span className="h-px w-8 bg-accent" /> Compare
            </span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08]">
              How CoSec App compares to{" "}
              <span className="text-accent">other platforms.</span>
            </h2>
            <p className="mt-4 text-white/70 max-w-2xl">
              Most UK Cosec teams evaluate two or three platforms before they decide. Here&apos;s a straight comparison of where CoSec App fits in the landscape.
            </p>
          </div>

          <div className="mt-10 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 font-display font-bold text-white/90 text-xs uppercase tracking-wider">Capability</th>
                  <th className="text-left p-4 font-display font-bold text-accent text-xs uppercase tracking-wider">CoSec App</th>
                  <th className="text-left p-4 font-display font-bold text-white/60 text-xs uppercase tracking-wider">Other platforms</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row, i) => (
                  <tr key={i} className={i % 2 ? "bg-white/[0.02]" : ""}>
                    <td className="p-4 text-white/90">{row.label}</td>
                    <td className="p-4"><CompareCell v={row.cs} /></td>
                    <td className="p-4 text-white/70"><CompareCell v={row.other} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-6 text-white/70 max-w-3xl">
            The short version: if you&apos;re a UK Cosec team that wants AI-assisted automation, secretary-first workflows and transparent pricing, CoSec App is built for you.
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}
